import { useEffect, useMemo, useRef, useState } from 'react';
import { apiClient, ApiError, type ProjectComment, type MemberRow } from '@/lib/api/client';
import { cn } from '@/lib/utils';
import { RelativeTime, DayDivider, dayKey } from '@/components/RelativeTime';
import { UserAvatar } from '@/components/UserAvatar';

/** Auto-grow cap for the composer: ~10 lines at leading-relaxed (1.625 *
 *  14px text-sm ≈ 22.75px per line) ≈ 230px. Keeps long pasted messages
 *  readable without consuming the whole chat pane. */
const MAX_COMPOSER_PX = 230;

interface Props {
  projectId: string;
  myUserId: string;
  /** Project members for @-mention resolution. */
  members: MemberRow[];
}

/**
 * Project chat — comments scoped to target_type='project'. Polled every 5s
 * for a near-realtime feel without WebSockets. Optimistic send appends the
 * message before the API round-trip; on failure we surface the error inline
 * and let the user retry.
 *
 * Author-only delete (matches the project/task delete policy). No mentions
 * / @-picker yet; that's a follow-up. Body is rendered as plain text with
 * preserved line-breaks (no markdown processor) — keeps the surface XSS-safe
 * via React's escaping until a real markdown pass lands.
 */
export function ProjectChat({ projectId, myUserId, members }: Props) {
  const [comments, setComments] = useState<ProjectComment[] | null>(null);
  const [myRole, setMyRole] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  /** When set, the composer attaches reply_to_comment_id to the next send.
   *  Holds enough metadata to render the "replying to X" chip without a lookup. */
  const [replyTo, setReplyTo] = useState<
    | { comment_id: string; author: string; preview: string }
    | null
  >(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  // Used to suppress auto-scroll when the user has scrolled up to read history.
  const stickToBottomRef = useRef(true);

  const canModerate = myRole === 'owner' || myRole === 'admin';

  async function refresh(silent = false) {
    try {
      const r = await apiClient.listProjectComments(projectId);
      setComments(r.comments);
      setMyRole(r.my_role);
      if (!silent) setErr(null);
    } catch (e) {
      if (!silent) setErr(e instanceof ApiError ? e.message : 'Failed to load chat');
    }
  }

  useEffect(() => {
    refresh();
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') refresh(true);
    }, 5000);
    function onVis() {
      if (document.visibilityState === 'visible') refresh(true);
    }
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Auto-scroll to bottom on new messages, but only if the user was already
  // pinned to the bottom (haven't scrolled up to read history).
  useEffect(() => {
    if (!comments || !scrollRef.current) return;
    if (stickToBottomRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [comments]);

  // Composer auto-grow: re-measure scrollHeight on every keystroke and set
  // explicit pixel height (capped at MAX_COMPOSER_PX = ~10 lines). Handles
  // both hard \n breaks AND soft wraps so a long sentence stays in view
  // instead of being hidden behind the textarea's internal scrollbar.
  // The "auto" reset is required so shrinking when the user deletes lines
  // works — without it, scrollHeight stays at the previous max.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, MAX_COMPOSER_PX) + 'px';
  }, [draft]);

  function onScroll(e: React.UIEvent<HTMLDivElement>) {
    const el = e.currentTarget;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
    stickToBottomRef.current = atBottom;
  }

  async function send() {
    const body = draft.trim();
    if (!body) return;
    setSending(true);
    setErr(null);
    // Capture reply target at send-time so a cancel during the round-trip
    // doesn't strip it from the optimistic row.
    const replyTarget = replyTo?.comment_id ?? null;
    // Optimistic append — temporary id, replaced on success.
    const tempId = `temp-${Date.now()}`;
    const optimistic: ProjectComment = {
      comment_id: tempId,
      user_id: myUserId,
      user_email: 'you',
      user_name: null,
      body_md: body,
      created_at: Date.now(),
      updated_at: Date.now(),
      reply_to_comment_id: replyTarget,
    };
    setComments((c) => (c ? [...c, optimistic] : [optimistic]));
    setDraft('');
    setReplyTo(null);
    stickToBottomRef.current = true;
    try {
      const r = await apiClient.createProjectComment(projectId, body, replyTarget);
      setComments((c) => {
        if (!c) return c;
        return c.map((x) => (x.comment_id === tempId ? r.comment : x));
      });
    } catch (e) {
      // Roll back the optimistic insert + restore draft (and reply target) so
      // the user can retry.
      setComments((c) => (c ? c.filter((x) => x.comment_id !== tempId) : c));
      setDraft(body);
      if (replyTarget && optimistic.reply_to_comment_id) {
        // Re-derive author/preview from the still-present parent.
        const parent = comments?.find((x) => x.comment_id === replyTarget);
        if (parent) {
          setReplyTo({
            comment_id: replyTarget,
            author: parent.user_name || parent.user_email,
            preview: parent.body_md.length > 60 ? parent.body_md.slice(0, 60) + '…' : parent.body_md,
          });
        }
      }
      setErr(e instanceof ApiError ? e.message : 'Send failed');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }

  function startReply(c: ProjectComment) {
    setReplyTo({
      comment_id: c.comment_id,
      author: c.user_name || c.user_email,
      preview: c.body_md.length > 60 ? c.body_md.slice(0, 60) + '…' : c.body_md,
    });
    inputRef.current?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  async function destroy(id: string) {
    if (pendingDelete !== id) {
      setPendingDelete(id);
      setTimeout(() => {
        setPendingDelete((curr) => (curr === id ? null : curr));
      }, 3000);
      return;
    }
    setPendingDelete(null);
    try {
      await apiClient.deleteComment(id);
      setComments((c) => (c ? c.filter((x) => x.comment_id !== id) : c));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Delete failed');
    }
  }

  function startEdit(c: ProjectComment) {
    setEditingId(c.comment_id);
    setEditDraft(c.body_md);
  }
  function cancelEdit() {
    setEditingId(null);
    setEditDraft('');
  }
  async function commitEdit(id: string) {
    const next = editDraft.trim();
    if (!next) {
      cancelEdit();
      return;
    }
    const original = comments?.find((x) => x.comment_id === id);
    if (!original || next === original.body_md) {
      cancelEdit();
      return;
    }
    // Optimistic update + bump updated_at to "now"
    setComments((cs) =>
      cs
        ? cs.map((x) =>
            x.comment_id === id ? { ...x, body_md: next, updated_at: Date.now() } : x,
          )
        : cs,
    );
    cancelEdit();
    try {
      await apiClient.editComment(id, next);
    } catch (e) {
      // Roll back optimistic body so server-side state remains source of truth
      setComments((cs) =>
        cs ? cs.map((x) => (x.comment_id === id ? { ...x, body_md: original.body_md, updated_at: original.updated_at } : x)) : cs,
      );
      setErr(e instanceof ApiError ? e.message : 'Edit failed');
    }
  }

  // Group consecutive comments from the same author for a tighter scan rhythm
  // (only re-show the avatar + name on author change or after >5min gap).
  const groups = useMemo(() => {
    if (!comments) return null;
    type Group = { user_id: string; user_email: string; user_name: string | null; items: ProjectComment[] };
    const out: Group[] = [];
    for (const c of comments) {
      const prev = out[out.length - 1];
      const sameAuthor = prev && prev.user_id === c.user_id;
      const sameWindow =
        prev && c.created_at - prev.items[prev.items.length - 1]!.created_at < 5 * 60 * 1000;
      if (sameAuthor && sameWindow) {
        prev.items.push(c);
      } else {
        out.push({
          user_id: c.user_id,
          user_email: c.user_email,
          user_name: c.user_name,
          items: [c],
        });
      }
    }
    return out;
  }, [comments]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between border-b border-border/60 bg-card/30 px-4 py-2">
        <h2 className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Chat
        </h2>
        <span className="font-mono text-[10px] text-muted-foreground/60 tabular-nums">
          {comments?.length ?? 0}
        </span>
      </div>

      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-4"
      >
        {comments === null ? (
          <div className="space-y-2" aria-busy="true">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-12 animate-pulse rounded bg-card" />
            ))}
          </div>
        ) : comments.length === 0 ? (
          <div className="grid h-full place-items-center text-center text-xs text-muted-foreground/70">
            <div>
              <p className="mb-1 font-mono uppercase tracking-widest text-[10px]">No messages yet</p>
              <p>Start the conversation below.</p>
            </div>
          </div>
        ) : (
          groups!.map((g, gi) => {
            // Day-divider rule (Slack/iMessage): render before THIS group if
            // it falls on a different calendar day than the previous group.
            // First group always gets a divider so the user always knows what
            // day they're reading from.
            const groupTs = g.items[0]!.created_at;
            const prev = gi > 0 ? groups![gi - 1] : null;
            const showDivider = !prev || dayKey(prev.items[prev.items.length - 1]!.created_at) !== dayKey(groupTs);
            return (
              <div key={`${g.user_id}-${gi}`}>
                {showDivider && <DayDivider ts={groupTs} />}
                <div className={cn('flex gap-3', showDivider && 'pt-2')}>
                  {(() => {
                    const m = members.find((x) => x.user_id === g.user_id);
                    return (
                      <UserAvatar
                        email={g.user_email}
                        image_key={m?.image_key}
                        avatar_url={m?.avatar_url}
                        size={28}
                        title={g.user_email}
                      />
                    );
                  })()}
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-baseline gap-2">
                      <span className="truncate text-xs font-semibold text-foreground">
                        {g.user_name || g.user_email}
                      </span>
                      <RelativeTime ts={groupTs} mode="time" />
                    </div>
                    {g.items.map((c) => {
                  const isMine = c.user_id === myUserId;
                  const isTemp = c.comment_id.startsWith('temp-');
                  const isEditing = editingId === c.comment_id;
                  const wasEdited = c.updated_at > c.created_at + 1000; // 1s grace for clock drift
                  const canDelete = !isTemp && (isMine || canModerate);
                  return (
                    <div key={c.comment_id} className="group/msg flex items-start gap-2">
                      {isEditing ? (
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            commitEdit(c.comment_id);
                          }}
                          className="min-w-0 flex-1 space-y-1"
                        >
                          <textarea
                            autoFocus
                            value={editDraft}
                            onChange={(e) => setEditDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') {
                                e.preventDefault();
                                cancelEdit();
                              }
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                commitEdit(c.comment_id);
                              }
                            }}
                            rows={Math.min(6, Math.max(2, editDraft.split('\n').length))}
                            maxLength={4000}
                            className="block w-full resize-none rounded-md border border-ring/40 bg-card px-2 py-1.5 text-sm text-foreground focus:border-ring focus:outline-none"
                          />
                          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70">
                            <span>Enter to save · Esc to cancel</span>
                          </div>
                        </form>
                      ) : (
                        <div className="min-w-0 flex-1">
                          {c.reply_to_comment_id && (
                            <ReplyQuote
                              parent={comments?.find((x) => x.comment_id === c.reply_to_comment_id) ?? null}
                            />
                          )}
                          <p className="whitespace-pre-wrap break-words text-sm text-foreground">
                            <MessageBody body={c.body_md} members={members} myUserId={myUserId} />
                            {wasEdited && (
                              <span
                                className="ml-1.5 font-mono text-[10px] text-muted-foreground/60"
                                title={`Edited ${new Date(c.updated_at).toLocaleString()}`}
                              >
                                (edited)
                              </span>
                            )}
                          </p>
                        </div>
                      )}
                      {!isEditing && (
                        <div className="flex shrink-0 items-center gap-1 opacity-0 group-hover/msg:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100">
                          {!isTemp && (
                            <button
                              type="button"
                              onClick={() => startReply(c)}
                              className="rounded px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-muted-foreground/60 transition-colors hover:text-foreground"
                              title="Reply"
                            >
                              reply
                            </button>
                          )}
                          {isMine && !isTemp && (
                            <button
                              type="button"
                              onClick={() => startEdit(c)}
                              className="rounded px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-muted-foreground/60 transition-colors hover:text-foreground"
                              title="Edit"
                            >
                              edit
                            </button>
                          )}
                          {canDelete && (
                            <button
                              type="button"
                              onClick={() => destroy(c.comment_id)}
                              className={cn(
                                'rounded px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest transition-colors',
                                pendingDelete === c.comment_id
                                  ? 'bg-destructive/15 text-destructive border border-destructive/70'
                                  : 'text-muted-foreground/60 hover:text-destructive',
                              )}
                              title={
                                pendingDelete === c.comment_id
                                  ? 'Click again to confirm'
                                  : !isMine
                                    ? 'Delete (admin)'
                                    : 'Delete'
                              }
                            >
                              {pendingDelete === c.comment_id ? 'confirm' : 'del'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {err && (
        <div role="alert" className="border-t border-destructive/40 bg-destructive/10 px-4 py-2 text-xs text-foreground">
          {err}
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="border-t border-border/60 bg-card/30 px-3 py-2"
      >
        {replyTo && (
          <div className="mb-2 flex items-start gap-2 rounded-md border-l-2 border-primary/70 bg-primary/5 px-2.5 py-1.5 text-xs">
            <div className="min-w-0 flex-1">
              <div className="font-mono text-[9px] uppercase tracking-widest text-primary/80">
                ↪ Replying to {replyTo.author}
              </div>
              <div className="truncate text-muted-foreground" title={replyTo.preview}>
                {replyTo.preview}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setReplyTo(null)}
              aria-label="Cancel reply"
              title="Cancel reply (Esc)"
              className="grid size-5 shrink-0 place-items-center rounded text-muted-foreground/60 transition-colors hover:bg-accent hover:text-foreground"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        )}
        {/* Composer with a real card look — bordered, padded, focus-glow
            via section-glow so it pulses when active just like every other
            input in the app. Send affordance on the right. */}
        <div className="section-glow flex items-end gap-2 rounded-md bg-background/40 px-3 py-2">
          <label htmlFor="project-chat-input" className="sr-only">
            Message
          </label>
          <textarea
            id="project-chat-input"
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && replyTo) {
                e.preventDefault();
                setReplyTo(null);
                return;
              }
              onKeyDown(e);
            }}
            placeholder={replyTo ? `Reply to ${replyTo.author}…` : 'Message · Enter to send · Shift+Enter for newline'}
            rows={1}
            maxLength={4000}
            disabled={sending}
            // Opt out of the global input-glow (we glow on the parent
            // section-glow instead). max-height is enforced by the auto-sizer.
            className="no-glow block min-h-[28px] w-full resize-none overflow-y-auto border-0 bg-transparent text-sm leading-relaxed text-foreground placeholder:text-muted-foreground/50 focus:outline-none disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={sending || !draft.trim()}
            aria-label="Send message"
            title="Send (Enter)"
            className="grid size-7 shrink-0 self-end place-items-center rounded-md bg-primary text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-30"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * ReplyQuote — small inset bar shown above a message body when it's a reply.
 * Renders parent author + 100-char preview. Gracefully shows "[deleted]" if
 * the parent has been removed (FK is ON DELETE SET NULL, so a deleted parent
 * still leaves the reply standing alone).
 */
function ReplyQuote({ parent }: { parent: ProjectComment | null }) {
  if (!parent) {
    return (
      <div className="mb-1 rounded border-l-2 border-border bg-card/40 px-2 py-1 text-[11px] italic text-muted-foreground/60">
        ↪ Original message was deleted
      </div>
    );
  }
  const preview = parent.body_md.length > 100 ? parent.body_md.slice(0, 100) + '…' : parent.body_md;
  const author = parent.user_name || parent.user_email;
  return (
    <div
      className="mb-1 rounded border-l-2 border-primary/50 bg-card/40 px-2 py-1 text-[11px] text-muted-foreground"
      title={`Reply to ${author}: ${preview}`}
    >
      <span className="font-mono text-[9px] uppercase tracking-widest text-primary/70">
        ↪ {author}
      </span>{' '}
      <span className="line-clamp-1">{preview}</span>
    </div>
  );
}

function chatHue(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % 360;
}

/**
 * MessageBody — splits body on @mention patterns + renders matched
 * mentions as periwinkle pills. A mention "matches" if its handle is
 * exactly a project member's email (case-insensitive) or the email's
 * local-part (before the @). Unmatched @-words render as plain text.
 *
 * Pattern: `@` followed by either an email-shaped string (preferred when
 * unambiguous) or a slug-shaped handle. Conservatively only highlights
 * resolvable mentions to avoid false positives like email addresses
 * casually pasted into the body.
 */
function MessageBody({
  body,
  members,
  myUserId,
}: {
  body: string;
  members: MemberRow[];
  myUserId: string;
}) {
  // Build lookup tables once per render.
  const byEmail = new Map<string, MemberRow>();
  const byLocalPart = new Map<string, MemberRow>();
  for (const m of members) {
    byEmail.set(m.email.toLowerCase(), m);
    const local = m.email.split('@')[0]?.toLowerCase();
    if (local) byLocalPart.set(local, m);
  }
  // Match @ followed by an email (foo@bar.tld) OR a bare local-part
  // (alphanum/underscore/dot/dash). Greedy on email for unambiguity.
  const re = /@([a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|[a-zA-Z0-9._-]+)/g;
  const parts: React.ReactNode[] = [];
  let lastIdx = 0;
  let match: RegExpExecArray | null;
  let nthMention = 0;
  while ((match = re.exec(body)) !== null) {
    const handle = match[1]!;
    const member = byEmail.get(handle.toLowerCase()) ?? byLocalPart.get(handle.toLowerCase());
    if (member) {
      if (match.index > lastIdx) parts.push(body.slice(lastIdx, match.index));
      const isMe = member.user_id === myUserId;
      parts.push(
        <span
          key={`m-${nthMention++}`}
          className={cn(
            'rounded px-1 py-0.5 font-medium',
            isMe ? 'bg-primary/20 text-primary' : 'bg-foreground/10 text-foreground',
          )}
          title={member.email}
        >
          @{handle}
        </span>,
      );
      lastIdx = match.index + match[0]!.length;
    }
  }
  if (lastIdx < body.length) parts.push(body.slice(lastIdx));
  return <>{parts.length > 0 ? parts : body}</>;
}
